import { useLayoutEffect, useRef, useState } from "react";
import { Monitor, RefreshCw, Smartphone } from "lucide-react";

const VIEWPORTS = {
  desktop: { width: 1280, height: 900, label: "Desktop" },
  mobile: { width: 390, height: 780, label: "Phone" },
} as const;

type ViewportKey = keyof typeof VIEWPORTS;

interface HubPreviewFrameProps {
  /** Same-origin path, e.g. `/help/omni-projects`. */
  src: string;
  title: string;
}

/**
 * The real Genie Page, in a frame, next to the editor.
 *
 * Mounts the actual public route in a same-origin iframe and scales it to fit
 * rather than redrawing an approximation — an approximation drifts from the
 * page the moment either changes, which is the whole reason to show a preview.
 * Scale is capped at 1: magnifying would misrepresent type size.
 */
export const HubPreviewFrame = ({ src, title }: HubPreviewFrameProps) => {
  const shellRef = useRef<HTMLDivElement>(null);
  const [availableWidth, setAvailableWidth] = useState(0);
  const [viewport, setViewport] = useState<ViewportKey>("desktop");
  const [reloadToken, setReloadToken] = useState(0);

  useLayoutEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;
    // Content-box width in both paths (clientWidth includes the p-4 padding).
    setAvailableWidth(shell.clientWidth - 32);
    const observer = new ResizeObserver(([entry]) => setAvailableWidth(entry.contentRect.width));
    observer.observe(shell);
    return () => observer.disconnect();
  }, []);

  const { width, height } = VIEWPORTS[viewport];
  // Until the shell has been measured, don't let the frame claim its full
  // viewport width — a 1280px box in an auto-sized grid track widens the
  // track, the shell then measures that widened width, and the page scrolls
  // sideways forever. Below xl this column is the only one on the page.
  const scale = availableWidth > 0 ? Math.min(1, availableWidth / width) : 0;

  return (
    <div className="w-full min-w-0 max-w-full overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="flex items-center gap-2 border-b border-gray-200 bg-[#F9FAFC] px-4 py-3">
        <p className="min-w-0 flex-1 truncate rounded-lg bg-white px-3 py-1.5 font-mono text-xs text-gray-500 ring-1 ring-gray-200">
          helpgenie.ai{src}
        </p>
        {(Object.keys(VIEWPORTS) as ViewportKey[]).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setViewport(key)}
            aria-label={VIEWPORTS[key].label}
            aria-pressed={viewport === key}
            className={`shrink-0 rounded-lg p-1.5 transition-colors ${
              viewport === key ? "bg-white text-genie-primary shadow-sm" : "text-gray-400 hover:bg-white"
            }`}
          >
            {key === "desktop" ? <Monitor className="h-4 w-4" /> : <Smartphone className="h-4 w-4" />}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setReloadToken((token) => token + 1)}
          className="shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-white"
          aria-label="Reload the preview"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      <div ref={shellRef} className="w-full min-w-0 overflow-hidden bg-[#EEF1F6] p-4">
        <div
          className="mx-auto max-w-full overflow-hidden rounded-xl bg-white ring-1 ring-gray-200"
          style={{ width: width * scale, height: height * scale }}
        >
          <iframe
            key={reloadToken}
            src={src}
            title={title}
            className="border-0"
            style={{ width, height, transform: `scale(${scale})`, transformOrigin: "top left" }}
          />
        </div>
      </div>
    </div>
  );
};

export default HubPreviewFrame;
